/**
 * CameraCapture - real in-app camera using getUserMedia (cross-platform).
 *
 * A file <input capture> is unreliable: on desktop it is ignored (the OS file
 * dialog opens, which looks like "the gallery"), and on some Android builds a
 * combined image+video accept opens a chooser. So we capture directly:
 *
 *   - tap the shutter        -> snap a photo (video frame -> canvas -> blob)
 *   - press & hold the shutter -> record a video (release to stop)
 *   - flip button             -> switch front/back camera
 *
 * On success it calls `onCapture({ kind, file })`; `onClose` dismisses without
 * capturing. The MediaStream is always stopped on unmount.
 */
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { IconX, IconSwitchCamera } from './icons.jsx';

const HOLD_MS = 280; // press longer than this starts a video recording

export function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const holdTimer = useRef(0);

  const [facing, setFacing] = useState('user');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);

  // (Re)open the stream whenever the facing mode changes.
  useEffect(() => {
    let cancelled = false;
    const stop = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    setReady(false);
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not available on this device.');
      return undefined;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: facing }, audio: true })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setReady(true);
      })
      .catch(() => setError('Camera permission denied.'));
    return () => {
      cancelled = true;
      stop();
    };
  }, [facing]);

  const snapPhoto = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1); // un-mirror the selfie preview
    }
    ctx.drawImage(v, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
          onCapture?.({ kind: 'photo', file });
        }
      },
      'image/jpeg',
      0.9,
    );
  }, [facing, onCapture]);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || typeof MediaRecorder === 'undefined') return;
    chunksRef.current = [];
    let recorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch {
      return;
    }
    recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
      const ext = (recorder.mimeType || 'video/webm').includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `video_${Date.now()}.${ext}`, { type: blob.type });
      onCapture?.({ kind: 'video', file });
    };
    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
  }, [onCapture]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    setRecording(false);
  }, []);

  // Shutter gestures: hold to record, tap to snap.
  const onShutterDown = useCallback(() => {
    if (!ready) return;
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = 0;
      startRecording();
    }, HOLD_MS);
  }, [ready, startRecording]);

  const onShutterUp = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = 0;
      snapPhoto();
    } else if (recording) {
      stopRecording();
    }
  }, [recording, snapPhoto, stopRecording]);

  return (
    <div class="camera">
      <button type="button" class="camera__close" onClick={onClose} aria-label="Close camera">
        <IconX size={20} />
      </button>

      {error ? (
        <div class="camera__error">{error}</div>
      ) : (
        <video
          ref={videoRef}
          class={`camera__video ${facing === 'user' ? 'is-mirrored' : ''}`}
          playsinline
          muted
        />
      )}

      <div class="camera__controls">
        <button
          type="button"
          class="camera__flip"
          onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}
          aria-label="Flip camera"
          disabled={!!error}
        >
          <IconSwitchCamera size={22} />
        </button>
        <button
          type="button"
          class={`camera__shutter ${recording ? 'is-recording' : ''}`}
          onPointerDown={onShutterDown}
          onPointerUp={onShutterUp}
          onPointerLeave={onShutterUp}
          disabled={!ready}
          aria-label="Capture"
        />
        <span class="camera__hint">{recording ? 'Recording…' : 'Tap photo · hold video'}</span>
      </div>
    </div>
  );
}
